import { useState } from 'react';

import { TriangleAlert } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { useToast } from '../toast-context';

import { useDeleteCampaign } from './useCampaign';

/**
 * Destructive-confirmation modal for permanently deleting a campaign (SCRUM-813).
 *
 * Banking-grade destructive UX: a prominent modal that (a) states the action is IRREVERSIBLE and
 * lists its consequences, (b) names the campaign, (c) gates the destructive button behind a
 * typed-phrase confirmation (the operator must type the campaign name exactly — mirrors the
 * SCRUM-391 account-closure precedent), and (d) DEFAULTS to Cancel: Cancel is autofocused, and
 * Escape / overlay-click dismiss without deleting (radix Dialog behavior).
 *
 * Follows Ryan Lynch's BankIQ design system: shadcn Dialog shell, danger surfaced via the
 * `--danger`/`text-danger`/`bg-danger` tokens only (NO literal hex), a soft ghost Cancel + a
 * solid-danger confirm matching the CloseAccountPanel confirm button. Bank-Executive-only is
 * enforced SERVER-SIDE (the CampaignAdmin policy); this dialog is only reachable from the
 * Executive-gated button, and a rejected DELETE surfaces as an inline error rather than navigating.
 *
 * The width override targets the `sm:` variant too — DialogContent ships `sm:max-w-sm`, so a plain
 * `max-w` would lose the cascade (SCRUM-209 responsive gotcha).
 */
export function DeleteCampaignDialog({
  campaignId,
  campaignName,
  outcomeCount,
  onClose,
  onDeleted,
}: {
  campaignId: string;
  campaignName: string;
  outcomeCount: number;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const { showToast } = useToast();
  const deleteCampaign = useDeleteCampaign(campaignId);
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);

  const confirmed = typed.trim() === campaignName.trim();
  const isPending = deleteCampaign.isPending;

  const submit = () => {
    if (!confirmed || isPending) return;
    setError(null);
    deleteCampaign.mutate(undefined, {
      onSuccess: () => {
        showToast({
          title: 'Campaign deleted',
          body: `“${campaignName}” was permanently removed.`,
        });
        onDeleted();
      },
      onError: (e) => {
        setError(e instanceof Error ? e.message : 'Failed to delete campaign.');
      },
    });
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !isPending) onClose();
      }}
    >
      <DialogContent className="w-[480px] max-w-[min(480px,calc(100vw-2rem))] gap-0 p-0 sm:max-w-[480px]">
        <DialogHeader className="border-border-0 border-b px-5 py-4">
          <DialogTitle className="text-danger font-display flex items-center gap-2 text-[1rem] font-semibold">
            <TriangleAlert className="h-4 w-4" aria-hidden="true" />
            Delete this campaign?
          </DialogTitle>
          <DialogDescription className="text-text-2 font-body pt-1 text-[0.8125rem] leading-relaxed">
            This permanently deletes the campaign{' '}
            <span className="text-text-0 font-semibold">“{campaignName}”</span> and cannot be
            undone.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 px-5 py-4">
          <div className="border-danger/20 bg-danger/5 rounded-tk-md border p-3">
            <p className="text-text-2 font-body text-[0.75rem] font-semibold tracking-[0.04em] uppercase">
              This will permanently remove
            </p>
            <ul className="text-text-1 font-body mt-2 flex list-disc flex-col gap-1 pl-4 text-[0.8125rem]">
              <li>The campaign and its configuration</li>
              <li>
                {outcomeCount === 1
                  ? '1 tracked customer outcome'
                  : `${String(outcomeCount)} tracked customer outcomes`}
              </li>
              <li>All outreach history recorded for this campaign</li>
            </ul>
            <p className="text-danger font-body mt-2 text-[0.75rem] font-medium">
              This action is irreversible. There is no way to recover a deleted campaign.
            </p>
          </div>

          <label className="text-text-3 flex flex-col gap-1.5 font-mono text-[0.625rem] font-semibold tracking-[0.06em] uppercase">
            Type the campaign name to confirm
            <input
              aria-label="Type the campaign name to confirm"
              type="text"
              value={typed}
              placeholder={campaignName}
              onChange={(e) => {
                setTyped(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submit();
              }}
              className="bg-bg-3 border-border-1 focus:border-danger rounded-tk-sm text-text-0 font-body border px-2.5 py-1.5 text-[0.8125rem] font-medium outline-none"
            />
          </label>

          {error && <p className="text-danger font-body text-[0.75rem]">{error}</p>}
        </div>

        {/*
          Cancel-and-return is the DEFAULT outcome by construction, without an a11y-flagged
          autoFocus: (1) the destructive button is DISABLED until the campaign name is typed
          exactly, so it is never the default action; (2) radix Dialog focuses the confirm INPUT
          on open (first focusable), so a stray Enter hits the typed-phrase guard, not delete;
          (3) Escape and overlay-click dismiss (radix) — every "do nothing" path returns safely.
        */}
        <DialogFooter className="border-border-0 flex justify-end gap-2 border-t px-5 pt-3 pb-4">
          <button
            type="button"
            disabled={isPending}
            onClick={onClose}
            className="border-border-1 bg-bg-3 text-text-1 hover:text-text-0 hover:bg-bg-2 rounded-tk-sm font-body border px-3.5 py-1.5 text-[0.75rem] font-medium disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!confirmed || isPending}
            onClick={submit}
            className="bg-danger rounded-tk-sm px-3.5 py-1.5 text-[0.75rem] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            {isPending ? 'Deleting…' : 'Delete campaign'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
